import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { trackPageView } from './lib/analytics'
import { AuthProvider, useAuth } from './context/AuthContext'
import { RoleGuard } from './components/RoleGuard'
import { ALL, OPS, OPS_ACCT, OPS_MKT, SUPER } from './lib/admin-roles'
import Layout from './components/Layout'
import Login from './pages/Login'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import AcceptInvite from './pages/AcceptInvite'
import Dashboard from './pages/Dashboard'
import Orders from './pages/Orders'
import Products from './pages/Products'
import Categories from './pages/Categories'
import Promotions from './pages/Promotions'
import Customers from './pages/Customers'
import Loyalty from './pages/Loyalty'
import Addons from './pages/Addons'
import Games from './pages/Games'
import Prizes from './pages/Prizes'
import DiscountCodes from './pages/DiscountCodes'
import GiftCards from './pages/GiftCards'
import Vouchers from './pages/Vouchers'
import Bundles from './pages/Bundles'
import Settings from './pages/Settings'
import AdminUsers from './pages/AdminUsers'
import MarketingSite from './pages/MarketingSite'
import SmsLog from './pages/SmsLog'
import PriceTags from './pages/PriceTags'
import Notifications from './pages/Notifications'
import NotificationsRouting from './pages/NotificationsRouting'
import WhatsAppCampaigns from './pages/WhatsAppCampaigns'
import Feedback from './pages/Feedback'
import ProductImports from './pages/ProductImports'
import StockLedger from './pages/StockLedger'
import PhoneAllowlist from './pages/PhoneAllowlist'
import Profile from './pages/Profile'
import Dispatch from './pages/Dispatch'
import Drivers from './pages/Drivers'
import Cashiers from './pages/Cashiers'
import Stores from './pages/Stores'
import Suppliers from './pages/Suppliers'
import PurchaseOrders from './pages/PurchaseOrders'
import StockCounts from './pages/StockCounts'
import StockTransfers from './pages/StockTransfers'
import AccountsPayable from './pages/AccountsPayable'
import PnL from './pages/PnL'
import CashFlow from './pages/CashFlow'
import PosSales from './pages/PosSales'
import PosSalesDetail from './pages/PosSalesDetail'
import PrintReliability from './pages/PrintReliability'
import Shifts from './pages/Shifts'
import DeliveryBalance from './pages/DeliveryBalance'
import Performance from './pages/Performance'
import Issues from './pages/Issues'
import Referrals from './pages/Referrals'
import LiveMap from './pages/LiveMap'
import GamesAnalytics from './pages/GamesAnalytics'
import Catering from './pages/Catering'
import Events from './pages/Events'
import Bookings from './pages/Bookings'
import Experiences from './pages/Experiences'
import Sales from './pages/Sales'
import CustomerTrends from './pages/CustomerTrends'
import AbandonedCarts from './pages/AbandonedCarts'
import AiLogs from './pages/AiLogs'
import Recommendations from './pages/Recommendations'
import Support from './pages/Support'
import KnowledgeBase from './pages/KnowledgeBase'
import PermissionsMatrix from './pages/PermissionsMatrix'
import AuditLog from './pages/AuditLog'
import DataPurge from './pages/DataPurge'
import PciTamperDetection from './pages/PciTamperDetection'
import { useAdminEvents } from './lib/events'

function ProtectedRoutes() {
  const { user, token, isLoading } = useAuth()
  // Open the realtime events socket only while an authenticated admin
  // is in the app. Hook handles reconnection internally; cleanup runs
  // when the token changes (sign-out) or the component unmounts.
  useAdminEvents(user ? token : null)
  if (isLoading) return <div className="h-screen bg-[#111111] flex items-center justify-center text-gray-500">Loading…</div>
  if (!user) return <Navigate to="/login" replace />
  return <Layout />
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()
  if (isLoading) return null
  if (user) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

/// Listens to BrowserRouter location changes and fires a GA4
/// `page_view` event per navigation. Lives inside BrowserRouter so it
/// can use `useLocation`; no-op when GA4 isn't configured.
function RouteAnalytics() {
  const location = useLocation()
  useEffect(() => {
    trackPageView(location.pathname + location.search)
  }, [location.pathname, location.search])
  return null
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RouteAnalytics />
        <Routes>
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route element={<ProtectedRoutes />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            {/* Universally accessible — every admin role can land here, so /dashboard
                is also the safe redirect target on a role mismatch elsewhere. */}
            <Route path="/dashboard"        element={<RoleGuard roles={ALL}><Dashboard /></RoleGuard>} />
            <Route path="/customer-trends"  element={<RoleGuard roles={ALL}><CustomerTrends /></RoleGuard>} />
            <Route path="/kb"               element={<RoleGuard roles={ALL}><KnowledgeBase /></RoleGuard>} />
            <Route path="/kb/:section/:slug" element={<RoleGuard roles={ALL}><KnowledgeBase /></RoleGuard>} />
            <Route path="/profile"          element={<RoleGuard roles={ALL}><Profile /></RoleGuard>} />

            {/* OPS — operations + super_admin */}
            <Route path="/products"         element={<RoleGuard roles={OPS}><Products /></RoleGuard>} />
            <Route path="/stock-ledger"     element={<RoleGuard roles={OPS}><StockLedger /></RoleGuard>} />
            <Route path="/product-imports"  element={<RoleGuard roles={OPS}><ProductImports /></RoleGuard>} />
            <Route path="/phone-allowlist"  element={<RoleGuard roles={OPS}><PhoneAllowlist /></RoleGuard>} />
            <Route path="/categories"       element={<RoleGuard roles={OPS}><Categories /></RoleGuard>} />
            <Route path="/addons"           element={<RoleGuard roles={OPS}><Addons /></RoleGuard>} />
            <Route path="/dispatch"         element={<RoleGuard roles={OPS}><Dispatch /></RoleGuard>} />
            <Route path="/live-map"         element={<RoleGuard roles={OPS}><LiveMap /></RoleGuard>} />
            <Route path="/drivers"          element={<RoleGuard roles={OPS}><Drivers /></RoleGuard>} />
            <Route path="/stores"           element={<RoleGuard roles={OPS}><Stores /></RoleGuard>} />
            <Route path="/suppliers"        element={<RoleGuard roles={OPS}><Suppliers /></RoleGuard>} />
            <Route path="/purchase-orders"  element={<RoleGuard roles={OPS}><PurchaseOrders /></RoleGuard>} />
            <Route path="/stock-counts"     element={<RoleGuard roles={OPS}><StockCounts /></RoleGuard>} />
            <Route path="/stock-transfers"  element={<RoleGuard roles={OPS}><StockTransfers /></RoleGuard>} />
            <Route path="/accounts-payable" element={<RoleGuard roles={OPS_ACCT}><AccountsPayable /></RoleGuard>} />
            <Route path="/pnl"              element={<RoleGuard roles={OPS_ACCT}><PnL /></RoleGuard>} />
            <Route path="/cash-flow"        element={<RoleGuard roles={OPS_ACCT}><CashFlow /></RoleGuard>} />
            <Route path="/delivery-balance" element={<RoleGuard roles={OPS_ACCT}><DeliveryBalance /></RoleGuard>} />
            <Route path="/cashiers"         element={<RoleGuard roles={OPS}><Cashiers /></RoleGuard>} />
            <Route path="/pos-sales"        element={<RoleGuard roles={OPS_ACCT}><PosSales /></RoleGuard>} />
            <Route path="/pos-sales-detail" element={<RoleGuard roles={OPS_ACCT}><PosSalesDetail /></RoleGuard>} />
            <Route path="/shifts"           element={<RoleGuard roles={OPS_ACCT}><Shifts /></RoleGuard>} />
            <Route path="/print-reliability" element={<RoleGuard roles={OPS}><PrintReliability /></RoleGuard>} />
            <Route path="/issues"           element={<RoleGuard roles={OPS}><Issues /></RoleGuard>} />
            <Route path="/support"          element={<RoleGuard roles={OPS}><Support /></RoleGuard>} />
            <Route path="/settings"         element={<RoleGuard roles={OPS}><Settings /></RoleGuard>} />

            {/* OPS_ACCT — operations + accounting + super_admin */}
            <Route path="/orders"           element={<RoleGuard roles={OPS_ACCT}><Orders /></RoleGuard>} />
            <Route path="/sales"            element={<RoleGuard roles={OPS_ACCT}><Sales /></RoleGuard>} />
            <Route path="/performance"      element={<RoleGuard roles={OPS_ACCT}><Performance /></RoleGuard>} />
            <Route path="/customers"        element={<RoleGuard roles={OPS_ACCT}><Customers /></RoleGuard>} />
            <Route path="/loyalty"          element={<RoleGuard roles={OPS_ACCT}><Loyalty /></RoleGuard>} />
            <Route path="/referrals"        element={<RoleGuard roles={OPS_ACCT}><Referrals /></RoleGuard>} />
            <Route path="/catering"         element={<RoleGuard roles={OPS_ACCT}><Catering /></RoleGuard>} />
            <Route path="/gift-cards"       element={<RoleGuard roles={OPS_ACCT}><GiftCards /></RoleGuard>} />
            <Route path="/vouchers"         element={<RoleGuard roles={OPS_ACCT}><Vouchers /></RoleGuard>} />

            {/* OPS_MKT — operations + marketing + super_admin */}
            <Route path="/promotions"       element={<RoleGuard roles={OPS_MKT}><Promotions /></RoleGuard>} />
            <Route path="/recommendations"  element={<RoleGuard roles={OPS_MKT}><Recommendations /></RoleGuard>} />
            <Route path="/discount-codes"   element={<RoleGuard roles={OPS_MKT}><DiscountCodes /></RoleGuard>} />
            <Route path="/bundles"          element={<RoleGuard roles={OPS_MKT}><Bundles /></RoleGuard>} />
            <Route path="/games"            element={<RoleGuard roles={OPS_MKT}><Games /></RoleGuard>} />
            <Route path="/prizes"           element={<RoleGuard roles={OPS_MKT}><Prizes /></RoleGuard>} />
            <Route path="/games-analytics"  element={<RoleGuard roles={OPS_MKT}><GamesAnalytics /></RoleGuard>} />
            <Route path="/experiences"      element={<RoleGuard roles={OPS_MKT}><Experiences /></RoleGuard>} />
            <Route path="/events"           element={<RoleGuard roles={OPS_MKT}><Events /></RoleGuard>} />
            <Route path="/bookings"         element={<RoleGuard roles={OPS_MKT}><Bookings /></RoleGuard>} />
            <Route path="/abandoned-carts"  element={<RoleGuard roles={OPS_MKT}><AbandonedCarts /></RoleGuard>} />
            <Route path="/notifications"    element={<RoleGuard roles={OPS_MKT}><Notifications /></RoleGuard>} />
            <Route path="/notifications-routing" element={<RoleGuard roles={OPS}><NotificationsRouting /></RoleGuard>} />
            <Route path="/whatsapp-campaigns" element={<RoleGuard roles={OPS_MKT}><WhatsAppCampaigns /></RoleGuard>} />
            <Route path="/feedback"         element={<RoleGuard roles={OPS_ACCT}><Feedback /></RoleGuard>} />
            <Route path="/marketing-site"   element={<RoleGuard roles={OPS_MKT}><MarketingSite /></RoleGuard>} />
            <Route path="/sms-log"          element={<RoleGuard roles={OPS}><SmsLog /></RoleGuard>} />
            <Route path="/price-tags"       element={<RoleGuard roles={OPS}><PriceTags /></RoleGuard>} />

            {/* SUPER — super_admin only */}
            <Route path="/admin-users"      element={<RoleGuard roles={SUPER}><AdminUsers /></RoleGuard>} />
            <Route path="/ai-logs"          element={<RoleGuard roles={SUPER}><AiLogs /></RoleGuard>} />
            <Route path="/permissions"      element={<RoleGuard roles={SUPER}><PermissionsMatrix /></RoleGuard>} />
            <Route path="/audit-log"        element={<RoleGuard roles={SUPER}><AuditLog /></RoleGuard>} />
            <Route path="/data-purge"       element={<RoleGuard roles={SUPER}><DataPurge /></RoleGuard>} />
            <Route path="/pci-tamper"       element={<RoleGuard roles={SUPER}><PciTamperDetection /></RoleGuard>} />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
